import { datadogRum } from '@datadog/browser-rum';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import { Member } from 'modules/common/interfaces/member';

/**
 * Sanitize member data by removing sensitive PII fields
 * Use this before storing member data in localStorage or other non-secure storage
 *
 * @param member - The member object to sanitize
 * @returns A sanitized copy of the member data without SSN, EIN, and DOB
 */
export const sanitizeMemberData = (member: Member): Partial<Member> => {
    // Destructure to exclude sensitive PII fields
    const {
        SSN, // Social Security Number
        EIN, // Employer Identification Number
        dob, // Date of Birth
        ...safeMemberData
    } = member;

    return safeMemberData;
};

/**
 * Cleanup function to remove existing member data from localStorage that contains SSN
 * This function should be called on app initialization to clean up legacy data
 *
 * TODO: Remove this function after 90 days of zero cleanup events (monitor via analytics)
 */
export const cleanupMemberDataWithSSN = (): void => {
    const keysWithSSN: string[] = [];

    Object.keys(localStorage).forEach((key) => {
        if (key.match(/^\d+\.member$/)) {
            try {
                const data = localStorage.getItem(key);
                if (data) {
                    const parsed = JSON.parse(data);
                    if (parsed.SSN) {
                        localStorage.removeItem(key);
                        keysWithSSN.push(key);
                    }
                }
            } catch (error) {
                sendDatadogRumError('Failed to parse member data during PII cleanup', error, {
                    key,
                });
            }
        }
    });

    // Track cleanup events to DataDog to monitor when all users have been migrated
    if (keysWithSSN.length > 0) {
        datadogRum.addAction('member_pii_cleanup_from_local_storage', {
            keys_cleaned: keysWithSSN.length,
            keys: keysWithSSN,
        });
    }
};

const PERSISTED_MEMBER_KEY_PATTERN = /^\d+\.(member|membershipInfo|oauth)$/;

/**
 * Remove all per-member localStorage keys cached by usePersistData.
 * Keys match the pattern {memberId}.member, {memberId}.membershipInfo, {memberId}.oauth.
 * These can accumulate across impersonation sessions and cause an QuotaExceededError.
 */
export const cleanupPersistedMemberData = (): void => {
    Object.keys(localStorage).forEach((key) => {
        if (PERSISTED_MEMBER_KEY_PATTERN.test(key)) {
            localStorage.removeItem(key);
        }
    });
};
